import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import { Modal } from '@mui/material';

import asyncService from '../../../../../datahub/asyncService';
import ModalBody from '../../../../components/BatchActionModal/components/ModalBody';
import selectUser from '../../../../store/selectors/appSelector';
import { BULK_ACTION_TYPES } from '../../timekeepingConstants';
import BatchModalBody from '../BatchModalBody';

import { getApiRoute, getPrimaryButtonText } from './sharedHelpers';

const BatchProcessModalByFilterV2 = ({
  isOpen,
  onClose,
  onGoBack,
  selectedFilters = [],
  initialTotalTimecardCount,
  initialTotalHourCount,
}) => {
  const dispatch = useDispatch();

  const { token, refreshToken } = useSelector(selectUser);

  const [isProcessing, setIsProcessing] = useState(false);
  const [isStartedProcessing, setIsStartedProcessing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [previousProcessedCount, setPreviousProcessedCount] = useState(null);
  const [isStuckAndForceAbort, setIsStuckAndForceAbort] = useState(false);
  const [failedCount, setFailedCount] = useState(0);
  const [succeededCount, setSucceededCount] = useState(0);

  const requestApi = useCallback(() => {
    asyncService({
      httpMethod: 'POST',
      route: getApiRoute(isOpen),
      data: {
        filters: selectedFilters,
      },
      onSuccess: (results) => {
        const newFailedCount = results?.data?.failed?.documents?.reduce(
          (accumulator, currentValue) => {
            const lengthOfTimecards = currentValue?.timecards?.length
              ? currentValue?.timecards?.length
              : 0;
            return accumulator + lengthOfTimecards;
          },
          0
        );

        const newSuccessCount = results?.data?.updated?.documents?.reduce(
          (accumulator, currentValue) => {
            const lengthOfTimecards = currentValue?.timecards?.length
              ? currentValue?.timecards?.length
              : 0;
            return accumulator + lengthOfTimecards;
          },
          0
        );

        const currentProcessedCount = newFailedCount + newSuccessCount;

        setSucceededCount(succeededCount + newSuccessCount);
        setFailedCount(failedCount + newFailedCount);

        // We will detect cases where we get stuck by seeing if we are not processing anything two times in a row.
        // This can happen in happy path if there are multiple users trying to perform this operation at the same time.
        if (currentProcessedCount === 0 && currentProcessedCount === previousProcessedCount) {
          setIsStuckAndForceAbort(true);
          setIsComplete(true);
        }
        setPreviousProcessedCount(currentProcessedCount);

        setIsProcessing(false);
      },
      onError: () => {
        setIsProcessing(false);
        setIsComplete(true);
        onClose();
      },
      dispatch,
      token,
      refreshToken,
    });
  }, [
    dispatch,
    failedCount,
    isOpen,
    onClose,
    previousProcessedCount,
    refreshToken,
    selectedFilters,
    succeededCount,
    token,
  ]);

  useEffect(() => {
    const handleBatchApiRequest = () => {
      // Go and get more items.
      setIsProcessing(true);
      requestApi();
    };

    if (failedCount + succeededCount >= initialTotalTimecardCount) {
      setIsComplete(true);
    }

    if (
      isStartedProcessing && // We can start processing
      !isProcessing && // We are not actively processing
      failedCount + succeededCount < initialTotalTimecardCount && // We didn't finish yet
      !isStuckAndForceAbort // We are not stuck
    ) {
      handleBatchApiRequest();
    }
  }, [
    failedCount,
    succeededCount,
    isProcessing,
    isStartedProcessing,
    isStuckAndForceAbort,
    requestApi,
    initialTotalTimecardCount,
  ]);

  // Handle case where modal is not open
  if (!isOpen) {
    return null;
  }
  // Handle case where we have a non-supported bulk action type
  if (
    isOpen !== BULK_ACTION_TYPES.RESOLVE &&
    isOpen !== BULK_ACTION_TYPES.APPROVE &&
    isOpen !== BULK_ACTION_TYPES.FINALIZE
  ) {
    // TODO: add DD log error
    return LanguageConverterV2('timekeeping.batchProcess.unsupportedBulkAction', {
      bulkActionType: isOpen,
    });
  }

  return (
    <Modal
      open={!!isOpen}
      onClose={() => {
        if (isProcessing) {
          return;
        }
        setIsProcessing(false);
        onClose();
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <ModalBody
        headerKey={getPrimaryButtonText(isOpen)}
        BodyElement={() => (
          <BatchModalBody
            totalTimecardCount={initialTotalTimecardCount}
            totalHoursWorked={initialTotalHourCount}
          />
        )}
        hasStarted={isProcessing}
        isComplete={isComplete}
        isProcessing={isProcessing}
        failedCount={failedCount}
        updatedCount={succeededCount}
        totalItems={initialTotalTimecardCount}
        cancelButtonKey="buttonText.goBack"
        onClose={onClose}
        onGoBack={onGoBack}
        primaryButtonKey={getPrimaryButtonText(isOpen)}
        handleConfirm={() => {
          // Begin processing.
          setIsStartedProcessing(true);
        }}
      />
    </Modal>
  );
};

BatchProcessModalByFilterV2.propTypes = {
  initialTotalHourCount: PropTypes.number.isRequired,
  initialTotalTimecardCount: PropTypes.number.isRequired,
  isOpen: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onGoBack: PropTypes.func.isRequired,
  selectedFilters: PropTypes.arrayOf(PropTypes.shape({})),
};

export default BatchProcessModalByFilterV2;
